import FileDownload from 'js-file-download';
import { IGetTransactionsRequest } from '@app/api/types';
import { axios } from '@app/services/axios';

const API_PREFIX = '/api/v2';

const connectApi = {
  user: {
    createEmailChangeRequest: async (data: {
      userId: string;
      email: string;
    }) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/users/${data.userId}/email/send-change-confirmation`,
        {
          email: data.email,
        }
      );
    },
    get: async (id: string) => {
      return axios.get(`${API_PREFIX}/zendesk/admin/users/${id}`);
    },
    create: async (data: any) => {
      return axios.post(`${API_PREFIX}/zendesk/admin/users`, data);
    },
    createNew: async (data: any) => {
      return axios.post(`${API_PREFIX}/admin/employees/create`, data);
    },
    update: async (userId: string, data: any) => {
      return axios.put(`${API_PREFIX}/zendesk/admin/users/${userId}`, data);
    },
    createPerson: async (userId: string, data: any) => {
      return axios.post(`${API_PREFIX}/zendesk/admin/persons/${userId}`, data);
    },
    updatePerson: async (userId: string, data: any) => {
      return axios.put(`${API_PREFIX}/zendesk/admin/persons/${userId}`, data);
    },
    createPersonIdentity: async (userId: string, data: any) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/persons/identity/${userId}`,
        data
      );
    },
    updatePersonIdentity: async (
      userId: string,
      documentId: string,
      data: any
    ) => {
      return axios.put(
        `${API_PREFIX}/zendesk/admin/persons/identity/${userId}/${documentId}`,
        data
      );
    },
    sendIDVerificationEmail: async (userId: string, companyId: string) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/persons/send-smile-verify?userId=${userId}&companyId=${companyId}`
      );
    },
    sendResetPasswordEmail: async (userId: string) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/${userId}/email/send-password-recovery`
      );
    },
    blockUnblock: async (userId: string, block = true) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/users/${userId}/${
          block ? 'block' : 'unblock'
        }`
      );
    },
    getAddress: async (userId: string) => {
      return axios.get(`${API_PREFIX}/zendesk/admin/addresses/user/${userId}`);
    },
    createAddress: async (userId: string, data: any) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/addresses/user/${userId}`,
        data
      );
    },
    updateAddress: async (userId: string, data: any) => {
      return axios.put(
        `${API_PREFIX}/zendesk/admin/addresses/user/${userId}`,
        data
      );
    },
  },
  company: {
    get: async (companyId: string) => {
      return axios.get(`${API_PREFIX}/zendesk/admin/companies/${companyId}`);
    },
    getListByPerson: async (personId: string) => {
      return axios.get(
        `${API_PREFIX}/zendesk/admin/companies/persons/${personId}/companies?personId=${personId}`
      );
    },
    filterByCreditCompanyId: async (creditApiCompanyId: any) => {
      return axios.get(
        `${API_PREFIX}/zendesk/admin/companies/filter?pageSize=1&page=1&creditApiCompanyId=${creditApiCompanyId}`
      );
    },
    update: async (companyId: string, data: any) => {
      return axios.put(
        `${API_PREFIX}/zendesk/admin/companies/${companyId}`,
        data
      );
    },
    getEmployees: async (companyId: string) => {
      return axios.get(`${API_PREFIX}/zendesk/admin/employees/${companyId}`);
    },
    createAddress: async (companyId: string, data: any) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/addresses/company/${companyId}`,
        data
      );
    },
    updateAddress: async (companyId: string, data: any) => {
      return axios.put(
        `${API_PREFIX}/zendesk/admin/addresses/company/${companyId}`,
        data
      );
    },
    getAddress: async (companyId: string) => {
      return axios.get(
        `${API_PREFIX}/zendesk/admin/addresses/company/${companyId}`
      );
    },
    setPrimaryUser: async (companyId: string, newUserId: string) => {
      return axios.post(
        `${API_PREFIX}/admin/companies/${companyId}/setprimaryuser/${newUserId}`
      );
    },
  },
  bankAccount: {
    getAll: async (companyId: string) => {
      return axios.get(`${API_PREFIX}/zendesk/admin/bankaccounts/${companyId}`);
    },
    createBankAccount: async (data: any) => {
      return axios.post(`${API_PREFIX}/zendesk/admin/traderoot/account`, data);
    },
    createTraderootMember: async (companyId: string) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/traderoot/company/${companyId}/createtraderootmember`
      );
    },
    blockAccount: async (companyId: string, accountNumber: string) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/traderoot/block-account?companyId=${companyId}&accountNumber=${accountNumber}`
      );
    },
    unblockAccount: async (companyId: string, accountNumber: string) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/traderoot/unblock-account?companyId=${companyId}&accountNumber=${accountNumber}`
      );
    },
  },
  transaction: {
    getTransactions: async (params: IGetTransactionsRequest) => {
      return axios.get(`${API_PREFIX}/admin/transactions/filter`, {
        params,
      });
    },
    downloadStatement: async (
      companyId: string,
      params: {
        bankAccountNumber: string;
        transactionDateFrom: string;
        transactionDateTo: string;
      }
    ) => {
      return axios.post(
        `${API_PREFIX}/admin/transactions/pdf/statements/${companyId}`,
        null,
        {
          params,
          responseType: 'blob',
        }
      );
    },
  },
  creditApi: {
    getFundingApplication: async (id: string) => {
      return axios.get(
        `${API_PREFIX}/creditapi/company/${id}/fundingapplication`
      );
    },
    getAdvance: async (id: string) => {
      return axios.get(`${API_PREFIX}/creditapi/advanceDetails/${id}`);
    },
  },
  document: {
    getDocumentsByCompany: async (id: string) => {
      return axios.get(`${API_PREFIX}/zendesk/admin/documents/bycompany/${id}`);
    },
    getDocumentsByUser: async (userId: string) => {
      return axios.get(
        `${API_PREFIX}/zendesk/admin/documents/byuser/${userId}`
      );
    },
    updateDocumentStatus: async (documentId: string, status: string) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/documents/${documentId}/status/${status}`
      );
    },
    downloadDocument: async (resourceId: string, name: string) => {
      return axios
        .get(`${API_PREFIX}/zendesk/admin/resources/${resourceId}`, {
          responseType: 'arraybuffer',
        })
        .then((response) => {
          FileDownload(response.data, name);
        });
    },
    upload: async (
      contactId: string,
      formData: FormData,
      onUploadProgress: any
    ) => {
      return axios.post(
        `${API_PREFIX}/zendesk/admin/documents/${contactId}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress,
        }
      );
    },
    delete: async (documentId: string) => {
      return axios.delete(
        `${API_PREFIX}/zendesk/admin/documents/${documentId}`
      );
    },
    update: async (
      documentId: string,
      params: { documentType: number; userId: string }
    ) => {
      return axios.put(
        `${API_PREFIX}/admin/documents/update/${documentId}`,
        null,
        { params }
      );
    },
  },
  employee: {
    get: async (companyId: string, userId: string) => {
      return axios.get(
        `${API_PREFIX}/zendesk/admin/employees/company/${companyId}/user/${userId}`
      );
    },
    assign: async (companyId: string, userId: string, position: number) => {
      return axios.post(`${API_PREFIX}/zendesk/admin/employees`, {
        companyId,
        userId,
        position,
      });
    },
    reassign: async (companyId: string, userId: string, position: number) => {
      return axios.put(`${API_PREFIX}/zendesk/admin/employees`, {
        userId,
        companyId,
        position,
        isPrivacyPolicySigned: true,
      });
    },
    unassign: async (companyId: string, userId: string) => {
      return axios.delete(
        `${API_PREFIX}/zendesk/admin/employees/company/${companyId}/user/${userId}`
      );
    },
  },
};

export default connectApi;
